import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPinned, BedDouble, BookOpen, Trash2, Bookmark, Share2, Check } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { SectionLabel } from '../components/shared';
import DayBuilder from '../components/DayBuilder';
import { useSaved } from '../context/SavedContext';

const TYPE_META = {
  emirate: { icon: MapPinned, label: 'Emirates' },
  hotel: { icon: BedDouble, label: 'Hotels' },
  guide: { icon: BookOpen, label: 'Guides' },
};

const Planner = () => {
  const { sid, items, remove, loading } = useSaved();
  const [copied, setCopied] = useState(false);
  useReveal([items.length, loading]);

  const shareUrl = `${window.location.origin}/shared/${sid}`;
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My UAE trip plan', url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    } catch (e) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } catch (e2) { /* ignore */ }
    }
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.type] = acc[it.type] || []).push(it);
    return acc;
  }, {});
  const order = ['emirate', 'hotel', 'guide'];

  return (
    <div className="bg-[#F6F1E3] min-h-[70vh]">
      <section className="bg-[#1B1D21]">
        <div className="max-w-5xl mx-auto px-5 py-16 text-center">
          <span className="text-[12px] font-semibold tracking-[0.18em] uppercase text-[#CBA344]">Your trip</span>
          <h1 className="mt-3 font-display text-[40px] sm:text-[48px] leading-[1.08] font-semibold text-white">
            Trip planner
          </h1>
          <p className="mt-4 text-[18px] text-[#c3d1de]">
            Everything you&apos;ve bookmarked — emirates, hotels and guides — in one place.
            Saved on this device.
          </p>
          {items.length > 0 && (
            <div className="mt-7 flex justify-center">
              <button
                onClick={share}
                className="btn-sunset inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold"
              >
                {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {copied ? 'Link copied!' : 'Share this trip'}
              </button>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-14">
        {items.length === 0 ? (
          <div className="reveal text-center py-16 rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2]">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F2ECCF] text-[#92722A]">
              <Bookmark className="w-7 h-7" />
            </span>
            <h2 className="mt-5 font-display text-[26px] font-semibold text-[#1B1D21]">Nothing saved yet</h2>
            <p className="mt-3 text-[16px] text-[#4B4F58] max-w-md mx-auto">
              Tap the bookmark icon on any emirate, hotel or guide and it&apos;ll appear here.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/emirates" className="btn-sunset rounded-full px-6 py-3 text-[15px] font-semibold">Browse the emirates</Link>
              <Link to="/hotels" className="btn-outline-ink rounded-full px-6 py-3 text-[15px] font-semibold">Find hotels</Link>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {order.filter((t) => grouped[t]?.length).map((type) => {
              const Meta = TYPE_META[type];
              const Icon = Meta.icon;
              return (
                <section key={type} className="reveal">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-[#e0564e]" />
                    <SectionLabel>{Meta.label}</SectionLabel>
                    <span className="text-[13px] text-[#8a7a63]">({grouped[type].length})</span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    {grouped[type].map((it) => (
                      <div key={it.id} className="group flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#E6D7A2] p-5 shadow-[0_14px_34px_-26px_rgba(27,29,33,0.5)]">
                        <Link to={it.to} className="min-w-0">
                          <h3 className="font-display text-[19px] font-semibold text-[#1B1D21] truncate">{it.title}</h3>
                          {it.subtitle ? (
                            <p className="mt-1 text-[14px] text-[#4B4F58] truncate">{it.subtitle}</p>
                          ) : null}
                        </Link>
                        <button
                          onClick={() => remove(it.id)}
                          aria-label="Remove"
                          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-[#9aa7b3] hover:text-white hover:bg-[#e0564e] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            <section className="reveal pt-4 border-t border-[#E6D7A2]">
              <DayBuilder sid={sid} savedItems={items} />
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Planner;
